/**
 * Étape 2 — Santé et alimentation.
 *
 * Deux écrans d'origine réunis : « Données santé » et « Allergies ». Pour le
 * parent c'est une seule question — ce que l'équipe doit savoir pour que son
 * enfant ne soit pas en danger — et la couper en deux n'obéissait qu'au
 * découpage des tables.
 *
 * Les questions de détail n'apparaissent que si elles ont lieu d'être : la
 * posologie quand il y a un médicament, la nature du régime quand il y en a un.
 * Un formulaire qui montre tout montre surtout ce qui ne concerne personne.
 */

import { Bloc, Case, Info, Navigation, Saisie, ZoneTexte } from './champs';
import { AVIS_DONNEES_SANTE, AVIS_MEDICAMENTS } from './textes';
import type { Erreurs, Sante } from './fiche';

const EtapeSante = ({
  valeur,
  onChange,
  erreurs,
  onPrecedent,
  onSuivant,
  prenom,
}: {
  valeur: Sante;
  onChange: (v: Sante) => void;
  erreurs: Erreurs;
  onPrecedent: () => void;
  onSuivant: () => void;
  prenom: string;
}) => {
  const maj = (champs: Partial<Sante>) => onChange({ ...valeur, ...champs });
  const enfant = prenom.trim() || 'votre enfant';

  return (
    <div className="space-y-8">
      <Info>{AVIS_DONNEES_SANTE}</Info>

      <Bloc
        titre="Ce que nous devons savoir"
        intro={`Si ${enfant} n'a rien de particulier, laissez ces champs vides — ce n'est pas un oubli.`}
      >
        <ZoneTexte
          label="Informations médicales importantes"
          facultatif
          valeur={valeur.infosMedicales}
          onChange={(v) => maj({ infosMedicales: v })}
          placeholder="Asthme, épilepsie, diabète… Précisez la conduite à tenir."
          aide="Ce qui peut survenir pendant la journée et ce que l’équipe doit faire."
        />
        <ZoneTexte
          label="Antécédents médicaux"
          facultatif
          valeur={valeur.antecedents}
          onChange={(v) => maj({ antecedents: v })}
          placeholder="Opération, fracture, hospitalisation… Ajoutez l’année si elle est utile."
        />
      </Bloc>

      <Bloc titre="Médicaments">
        <Info ton="attention">
          <p className="whitespace-pre-line">{AVIS_MEDICAMENTS}</p>
        </Info>

        <Case
          coche={valeur.medication}
          onChange={(v) => maj({ medication: v })}
          label={`${enfant} doit prendre un médicament pendant le stage`}
        />

        {valeur.medication && (
          <div className="space-y-5 border-l-2 border-primary-100 pl-4">
            <ZoneTexte
              label="Lequel, et à quelle posologie"
              requis
              valeur={valeur.medicationDetails}
              onChange={(v) => maj({ medicationDetails: v })}
              erreur={erreurs.medicationDetails}
              placeholder="Nom du médicament, dose, moment de la prise."
              aide="Nous vous enverrons le formulaire d’autorisation par e-mail. Signé, il est à remettre le premier jour."
            />
            <Case
              coche={valeur.medicationAutonomie}
              onChange={(v) => maj({ medicationAutonomie: v })}
              label="Peut le prendre sans l’aide d’un adulte"
            />
          </div>
        )}

        <Case
          coche={valeur.tetanos}
          onChange={(v) => maj({ tetanos: v })}
          label="Vaccin contre le tétanos à jour"
        />
      </Bloc>

      <Bloc
        titre="Son médecin"
        intro="Qui appeler en cas de besoin, avant de passer par les urgences."
      >
        <div className="grid gap-5 md:grid-cols-2">
          <Saisie
            label="Nom du médecin traitant"
            requis
            valeur={valeur.medecinNom}
            onChange={(v) => maj({ medecinNom: v })}
            erreur={erreurs.medecinNom}
            placeholder="Dr ..."
          />
          <Saisie
            label="Téléphone du médecin"
            facultatif
            type="tel"
            valeur={valeur.medecinTel}
            onChange={(v) => maj({ medecinTel: v })}
            placeholder="02 ..."
            autoComplete="off"
            aide="Si vous ne l’avez pas sous la main, l’équipe le retrouvera à partir du nom."
          />
        </div>
      </Bloc>

      <Bloc titre="Allergies et alimentation">
        <Case
          coche={valeur.allergies}
          onChange={(v) => maj({ allergies: v })}
          label={`${enfant} a une ou plusieurs allergies`}
        />

        {valeur.allergies && (
          <div className="space-y-5 border-l-2 border-primary-100 pl-4">
            <ZoneTexte
              label="À quoi"
              requis
              valeur={valeur.allergiesDetails}
              onChange={(v) => maj({ allergiesDetails: v })}
              erreur={erreurs.allergiesDetails}
              placeholder="Arachides, pollen, piqûres de guêpe…"
              lignes={3}
            />
            <ZoneTexte
              label="Ce qui se passe en cas de contact, et ce qu’il faut faire"
              requis
              valeur={valeur.allergiesConsequences}
              onChange={(v) => maj({ allergiesConsequences: v })}
              erreur={erreurs.allergiesConsequences}
              placeholder="Rougeurs, gonflement, difficulté à respirer… Présence d’un auto-injecteur, où il se trouve."
              lignes={3}
            />
          </div>
        )}

        <Case
          coche={valeur.regimeSpecial}
          onChange={(v) => maj({ regimeSpecial: v })}
          label={`${enfant} suit un régime alimentaire particulier`}
          aide="Convictions, intolérances, prescriptions."
        />

        {valeur.regimeSpecial && (
          <div className="border-l-2 border-primary-100 pl-4">
            <ZoneTexte
              label="Lequel"
              requis
              valeur={valeur.regimeDetails}
              onChange={(v) => maj({ regimeDetails: v })}
              erreur={erreurs.regimeDetails}
              placeholder="Sans porc, végétarien, sans lactose…"
              lignes={3}
            />
          </div>
        )}
      </Bloc>

      <Navigation onPrecedent={onPrecedent} onSuivant={onSuivant} />
    </div>
  );
};

export default EtapeSante;
